<script setup lang="ts">
import { ref, watch } from 'vue';
import useLocale from '../composables/useLocale';
import BinaryRating from '../binary-rating/BinaryRating.vue';

const props = defineProps<{
  show: boolean;
  contentId: string | number;
  rating?: 1 | 0 | null;
}>();

export type EmitRate = {
  contentId: string | number;
  value: 1 | 0 | null;
};

const { t } = useLocale();

const emit = defineEmits<{
  (e: 'rate', payload: EmitRate): void;
  (e: 'close'): void;
}>();

const rated = ref(false);

const handleRate = (payload: EmitRate) => {
  if (payload.value !== null) {
    rated.value = true;
    setTimeout(() => emit('close'), 1000);
  }
  emit('rate', payload);
};

watch(
  () => props.show,
  (isVisible) => {
    if (isVisible) {
      rated.value = false;
    }
  },
);
</script>

<template>
  <Transition name="overlay-fade">
    <div v-if="props.show" class="overlay-rating" role="dialog" aria-modal="true" aria-label="Rate this video">
      <div class="overlay-rating__content">
        <template v-if="!rated">
          <p class="overlay-rating__prompt text-white text-h6 font-weight-medium mb-6">
            {{ t('ratings.feedback_prompt') }}
          </p>
          <BinaryRating :content-id="props.contentId" :value="props.rating" @rate="handleRate" />
        </template>

        <template v-else>
          <v-icon icon="mdi-check-circle-outline" color="white" size="48" class="mb-3" />
          <p class="overlay-rating__prompt text-white text-h6 font-weight-medium">{{ t('ratings.thanks') }}</p>
        </template>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.overlay-rating {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  pointer-events: auto;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.25rem 1.5rem;
    text-align: center;
    background-color: rgba(0, 0, 0, 0.72);
    border-radius: 8px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
    min-width: 220px;
    max-width: 90vw;
  }
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}
</style>
